import { methodology } from './data/catalog'
import { formatIndicator, formatMoney } from './calculation'
import type { CalculationInput, CalculationResult } from './types'

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}

function csvCell(value: unknown): string {
  const text = String(value ?? '')
  return `"${text.replaceAll('"', '""')}"`
}

export function buildCsvContent(result: CalculationResult): string {
  const rows: unknown[][] = [
    ['Cenové ukazatele dle metodického pokynu', methodology.meta.methodology],
    ['Majetek', result.assetLabel],
    ['Vstupy', result.inputSummary],
    ['Stav výpočtu', result.methodLabel],
    [result.indicatorLabel, result.indicatorGross, result.indicatorUnit],
    ['Celková cena s DPH', result.totalGross],
    ['Celková cena bez DPH', result.totalNet],
    ['Vzorec', result.formula],
    ['Zdroj', result.sourceLabel],
    [],
    ['Použité zdrojové body'],
    ['Parametr', 'Hodnota'],
    ...result.sourcePoints.map((point) => [point.x, point.value]),
    [],
    ['Upozornění'],
    ...result.warnings.map((warning) => [warning]),
  ]
  return `\uFEFF${rows.map((row) => row.map(csvCell).join(';')).join('\r\n')}`
}

export function exportCsv(result: CalculationResult) {
  const csv = buildCsvContent(result)
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'cenovy-ukazatel-vak.csv')
}

export async function exportXlsx(input: CalculationInput, result: CalculationResult) {
  const { default: writeExcelFile } = await import('write-excel-file/browser')
  const header = { fontWeight: 'bold' as const, backgroundColor: '#E8F2F1', borderColor: '#B5CDCB' }
  const money = { type: Number, format: '#,##0" Kč"' }
  const indicator = { type: Number, format: result.indicatorUnit.startsWith('tis. Kč') ? '#,##0.000' : '#,##0.###' }
  const resultData = [
    [{ value: 'Cenové ukazatele dle metodického pokynu', fontWeight: 'bold' as const, fontSize: 14, columnSpan: 2 }],
    ['Metodický pokyn', methodology.meta.methodology],
    ['Verze dat', methodology.meta.dataVersion],
    ['Majetek', result.assetLabel],
    ['Vstupy', result.inputSummary],
    ['Stav výpočtu', result.methodLabel],
    [result.indicatorLabel, { value: result.indicatorGross, ...indicator }, result.indicatorUnit],
    ['Celková cena s 21 % DPH', { value: result.totalGross, ...money }],
    ['Celková cena bez DPH', { value: result.totalNet, ...money }],
    ['Vzorec', result.formula],
    ['Zdroj', result.sourceLabel],
    [],
    [{ value: 'Úpravy', ...header }, { value: 'Hodnota', ...header }],
    ...result.adjustments.map((value) => [value, '']),
    [],
    [{ value: 'Členění ceny', ...header }, { value: 'S DPH', ...header }, { value: 'Bez DPH', ...header }],
    ...result.breakdown.map((item) => [item.label, { value: item.gross, ...money }, { value: item.net, ...money }]),
    [],
    [{ value: 'Upozornění', ...header }],
    ...result.warnings.map((warning) => [warning]),
  ]
  const sourceData = [
    [{ value: 'Použité zdrojové body', fontWeight: 'bold' as const, fontSize: 14, columnSpan: 2 }],
    [{ value: 'Parametr', ...header }, { value: 'Hodnota', ...header }],
    ...result.sourcePoints.map((point) => [point.x, point.value]),
  ]
  const methodData = [
    [{ value: 'Metodika a audit výpočtu', fontWeight: 'bold' as const, fontSize: 14, columnSpan: 2 }],
    ['Metodický pokyn', methodology.meta.methodology],
    ['Datum zdroje', methodology.meta.sourceDate],
    ['Zdrojový soubor', methodology.meta.sourceFile],
    ['Verze dat', methodology.meta.dataVersion],
    ['Upozornění', methodology.meta.disclaimer],
    ['Zadaný majetek', input.assetId],
    ['Datum výpočtu', { value: new Date(result.calculatedAt), type: Date, format: 'dd.mm.yyyy hh:mm:ss' }],
    ['Vzorec', result.formula],
  ]
  await writeExcelFile([
    { data: resultData, sheet: 'Výpočet', columns: [{ width: 34 }, { width: 64 }, { width: 24 }], stickyRowsCount: 1 },
    { data: sourceData, sheet: 'Zdrojové body', columns: [{ width: 22 }, { width: 22 }], stickyRowsCount: 2 },
    { data: methodData, sheet: 'Metodika', columns: [{ width: 28 }, { width: 76 }], stickyRowsCount: 1 },
  ]).toFile('cenovy-ukazatel-vak.xlsx')
}

export async function copyResult(result: CalculationResult): Promise<void> {
  const warnings = result.warnings.length ? `\nUpozornění: ${result.warnings.join(' ')}` : ''
  const text = `${result.assetLabel}\n${result.inputSummary}\n${result.indicatorLabel}: ${formatIndicator(result.indicatorGross, result.indicatorUnit)}\nCelková cena s DPH: ${formatMoney(result.totalGross)}\nCelková cena bez DPH: ${formatMoney(result.totalNet)}\n${result.methodLabel}\n${result.sourceLabel}${warnings}`
  await navigator.clipboard.writeText(text)
}
